/**
 * Config for "Telegram Groups" video (MuxVideoPlayerWithCTA).
 * Used by: /grupo-privado, videoCTAConfig telegram.
 *
 * PRICING STRUCTURE:
 * - HIGH_PRICE ($29.99): US, Europe, MX, CO, PE, PY, BO, AE + default
 * - LOW_PRICE: CL, BR, UY
 * - ARGENTINA: Uses env var or fallback
 *
 * VIDEO LANGUAGE LOGIC:
 * - Spanish-speaking countries → Spanish video (VITE_TELEGRAM_GROUPS_PLAYBACK_ID)
 * - Europe (except ES) → English video (VITE_TELEGRAM_GROUPS_EN_PLAYBACK_ID)
 * - US, Middle East → English video
 * - Country-specific override if VITE_TELEGRAM_GROUPS_XX_PLAYBACK_ID exists
 */

import { appendBrandReferenceId } from "./stripeReferenceId";
import { BRAND_MUX_DEFAULT_PLAYBACK_ID } from "@/lib/brandConfig";

// =============================================================================
// DEBUG: Log raw env vars on module load (only in development)
// =============================================================================
if (import.meta.env.DEV) {
  console.log("[telegramGroupsVideo] Raw env vars:", {
    PLAYBACK_ID: import.meta.env.VITE_TELEGRAM_GROUPS_PLAYBACK_ID,
    EN_PLAYBACK_ID: import.meta.env.VITE_TELEGRAM_GROUPS_EN_PLAYBACK_ID,
    FALLBACK_PLAYBACK_ID: import.meta.env.VITE_TELEGRAM_GROUPS_FALLBACK_PLAYBACK_ID,
    types: {
      PLAYBACK_ID: typeof import.meta.env.VITE_TELEGRAM_GROUPS_PLAYBACK_ID,
      EN_PLAYBACK_ID: typeof import.meta.env.VITE_TELEGRAM_GROUPS_EN_PLAYBACK_ID,
    }
  });
}

// =============================================================================
// STRIPE PAYMENT LINKS (configurable via env vars)
// =============================================================================

// High price ($29.99) - Most countries (default)
const HIGH_PRICE_URL = "https://buy.stripe.com/28E14n1RIbDje110RV5wI3x";

// Low price - Chile, Brasil, Uruguay (default)
const LOW_PRICE_URL = "https://buy.stripe.com/8x29ATcwm7n35uvbwz5wI3w";

// Argentina fallback
const ARGENTINA_FALLBACK_URL = "https://buy.stripe.com/14A28raoe9vb5uvbwz5wI3r";

// Country-specific Stripe URLs (can be overridden by env vars)
const TELEGRAM_GROUPS_US_CTA_URL =
  (import.meta.env.VITE_TELEGRAM_GROUPS_US_CTA_URL as string) || HIGH_PRICE_URL;

const TELEGRAM_GROUPS_ES_CTA_URL =
  (import.meta.env.VITE_TELEGRAM_GROUPS_ES_CTA_URL as string) || HIGH_PRICE_URL;

const TELEGRAM_GROUPS_MX_CTA_URL =
  (import.meta.env.VITE_TELEGRAM_GROUPS_MX_CTA_URL as string) || HIGH_PRICE_URL;

const TELEGRAM_GROUPS_AE_CTA_URL =
  (import.meta.env.VITE_TELEGRAM_GROUPS_AE_CTA_URL as string) || HIGH_PRICE_URL;

const TELEGRAM_GROUPS_SA_CTA_URL =
  (import.meta.env.VITE_TELEGRAM_GROUPS_SA_CTA_URL as string) || HIGH_PRICE_URL;

// =============================================================================
// VIDEO PLAYBACK IDs
// =============================================================================

// Helper to get env var (returns empty string if not set or empty)
// Also validates that it's a valid Mux playback ID format
const getEnvVar = (envVar: string | undefined, varName?: string): string => {
  // Check if undefined or null first
  if (envVar === undefined || envVar === null) {
    if (varName && import.meta.env.DEV) {
      console.warn(`[telegramGroupsVideo] ${varName} is undefined or null`);
    }
    return "";
  }
  
  // Convert to string and trim
  const trimmed = String(envVar).trim();
  
  // Check if empty after trim
  if (trimmed.length === 0) {
    if (varName && import.meta.env.DEV) {
      console.warn(`[telegramGroupsVideo] ${varName} is empty string`);
    }
    return "";
  }
  
  // Validate it's not a placeholder value
  if (trimmed.includes("your-") || trimmed.includes("tu-") || trimmed.includes("example") || trimmed.includes("xxx")) {
    if (import.meta.env.DEV) {
      console.warn(`[telegramGroupsVideo] ${varName || 'Variable'} has placeholder value: "${trimmed.substring(0, 20)}..."`);
    }
    return "";
  }
  
  // Validate minimum length for Mux playback IDs (typically 40+ chars)
  if (trimmed.length < 10) {
    if (import.meta.env.DEV) {
      console.warn(`[telegramGroupsVideo] ${varName || 'Variable'} too short (${trimmed.length} chars): "${trimmed}"`);
    }
    return "";
  }
  
  // Success - only log in development
  if (varName && import.meta.env.DEV) {
    console.log(`[telegramGroupsVideo] ✅ ${varName} loaded: ${trimmed.substring(0, 20)}... (${trimmed.length} chars)`);
  }
  
  return trimmed;
};

// Fallback cuando ES/EN no están definidos (p.ej. AR en /ar/invitacion)
// Prioridad: env VITE_TELEGRAM_GROUPS_FALLBACK_PLAYBACK_ID > default del brand.
// Ahora viene de brandConfig (VITE_BRAND_MUX_DEFAULT_PLAYBACK_ID) para que cada
// fork sirva SU propio video en vez de un ID hardcodeado compartido.
export const HARDCODED_FALLBACK_PLAYBACK_ID = BRAND_MUX_DEFAULT_PLAYBACK_ID;

/** Playback ID para Argentina; usado por /ar/invitacion. Prioridad: VITE_TELEGRAM_GROUPS_AR_PLAYBACK_ID > español > fallback. */
const AR_PLAYBACK_ID =
  getEnvVar(import.meta.env.VITE_TELEGRAM_GROUPS_AR_PLAYBACK_ID as string, "VITE_TELEGRAM_GROUPS_AR_PLAYBACK_ID");

/** Playback ID VIP para /ar/invitacion (opción VIP). Definir VITE_AR_INVITACION_VIP_PLAYBACK_ID en .env; si no, usa el estándar AR. */
const AR_VIP_PLAYBACK_ID =
  getEnvVar(import.meta.env.VITE_AR_INVITACION_VIP_PLAYBACK_ID as string, "VITE_AR_INVITACION_VIP_PLAYBACK_ID");

const FALLBACK_PLAYBACK_ID =
  getEnvVar(import.meta.env.VITE_TELEGRAM_GROUPS_FALLBACK_PLAYBACK_ID as string, "VITE_TELEGRAM_GROUPS_FALLBACK_PLAYBACK_ID") ||
  // Playback default Telegram Groups (doc: TELEGRAM_CONFIGURATION.md)
  HARDCODED_FALLBACK_PLAYBACK_ID;

// Main playback ID (Spanish) - VITE_TELEGRAM_GROUPS_PLAYBACK_ID
// Si está vacío, usa FALLBACK
const SPANISH_PLAYBACK_RAW = getEnvVar(import.meta.env.VITE_TELEGRAM_GROUPS_PLAYBACK_ID as string, "VITE_TELEGRAM_GROUPS_PLAYBACK_ID");
export const TELEGRAM_GROUPS_PLAYBACK_ID = SPANISH_PLAYBACK_RAW || FALLBACK_PLAYBACK_ID;

if (!SPANISH_PLAYBACK_RAW && import.meta.env.DEV) {
  console.warn(`[telegramGroupsVideo] ⚠️ VITE_TELEGRAM_GROUPS_PLAYBACK_ID not configured, using FALLBACK: ${FALLBACK_PLAYBACK_ID.substring(0, 20)}...`);
}

// English playback ID - VITE_TELEGRAM_GROUPS_EN_PLAYBACK_ID
// Used as fallback for Europe (except Spain), US, Middle East
// Si está vacío, usa Spanish o FALLBACK
const ENGLISH_PLAYBACK_RAW = getEnvVar(import.meta.env.VITE_TELEGRAM_GROUPS_EN_PLAYBACK_ID as string, "VITE_TELEGRAM_GROUPS_EN_PLAYBACK_ID");
export const TELEGRAM_GROUPS_EN_PLAYBACK_ID = ENGLISH_PLAYBACK_RAW || TELEGRAM_GROUPS_PLAYBACK_ID;

if (!ENGLISH_PLAYBACK_RAW && import.meta.env.DEV) {
  console.warn(`[telegramGroupsVideo] ⚠️ VITE_TELEGRAM_GROUPS_EN_PLAYBACK_ID not configured, using ${SPANISH_PLAYBACK_RAW ? 'Spanish' : 'FALLBACK'}: ${TELEGRAM_GROUPS_EN_PLAYBACK_ID.substring(0, 20)}...`);
}

// =============================================================================
// COUNTRY/REGION DEFINITIONS
// =============================================================================

// Spanish-speaking countries (use Spanish video)
const SPANISH_SPEAKING_COUNTRIES = [
  "ES", // Spain
  "MX", "AR", "CO", "PE", "CL", "VE", "EC", "BO", "PY", "UY", // LATAM
  "CR", "PA", "DO", "GT", "HN", "NI", "SV", "CU", "PR", // Central America & Caribbean
  "LATAM", // Fallback cuando Worker no detecta país - video en español
];

// European countries (use English video, HIGH_PRICE)
const EUROPEAN_COUNTRIES = [
  "DE", "FR", "IT", "PT", "NL", "BE", "AT", "CH", "GB", "IE",
  "SE", "NO", "DK", "FI", "PL", "CZ", "GR", "RO", "HU", "SK",
  "BG", "HR", "SI", "LT", "LV", "EE", "LU", "MT", "CY", "IS", // Iceland
];

// Middle East countries (use English video, HIGH_PRICE)
const MIDDLE_EAST_COUNTRIES = ["AE", "SA", "QA", "KW", "BH", "OM"];

// Argentina CTA URL (only country with special env var override)
const TELEGRAM_GROUPS_AR_CTA_URL =
  (import.meta.env.VITE_TELEGRAM_GROUPS_AR_CTA_URL as string) || "";

// Export for backwards compatibility
export const TELEGRAM_GROUPS_CTA_URL = appendBrandReferenceId(HIGH_PRICE_URL);

/** True si hay playback para channel_invite (env o fallback AR). Usado por hasVideoConfigured. */
export function hasChannelInviteVideo(): boolean {
  return Boolean(
    TELEGRAM_GROUPS_PLAYBACK_ID ||
    TELEGRAM_GROUPS_EN_PLAYBACK_ID ||
    FALLBACK_PLAYBACK_ID
  );
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Check if a country is Spanish-speaking
 */
export function isSpanishSpeakingCountry(countryCode: string): boolean {
  return SPANISH_SPEAKING_COUNTRIES.includes(countryCode.toUpperCase());
}

/**
 * Check if a country is European (except Spain which is Spanish-speaking)
 */
export function isEuropeanCountry(countryCode: string): boolean {
  return EUROPEAN_COUNTRIES.includes(countryCode.toUpperCase());
}

/**
 * Check if a country is in Middle East
 */
export function isMiddleEastCountry(countryCode: string): boolean {
  return MIDDLE_EAST_COUNTRIES.includes(countryCode.toUpperCase());
}

/**
 * Get the appropriate fallback playback ID based on country
 * - Spanish-speaking → Spanish video, or English if Spanish not configured
 * - Europe, US, Middle East → English video
 * - GARANTIZA que siempre retorne un playbackId válido (nunca vacío)
 */
function getDefaultPlaybackIdForCountry(countryCode: string): string {
  const upperCode = countryCode.toUpperCase();
  
  // Argentina: playback específico para /ar/invitacion (VITE_TELEGRAM_GROUPS_AR_PLAYBACK_ID) si está definido
  if (upperCode === "AR" && AR_PLAYBACK_ID) {
    return AR_PLAYBACK_ID;
  }

  // Spanish-speaking countries: Spanish video, fallback to English, fallback to hardcoded
  if (isSpanishSpeakingCountry(upperCode)) {
    const main = TELEGRAM_GROUPS_PLAYBACK_ID || TELEGRAM_GROUPS_EN_PLAYBACK_ID || FALLBACK_PLAYBACK_ID;
    if (!main || main.trim().length === 0) {
      console.error(`[telegramGroupsVideo] All playback IDs empty for Spanish country ${upperCode} - critical config error`);
      return FALLBACK_PLAYBACK_ID; // Hardcoded fallback
    }
    return main;
  }
  
  // US, Europe, Middle East get English video, fallback to Spanish, fallback to hardcoded
  if (upperCode === "US" || isEuropeanCountry(upperCode) || isMiddleEastCountry(upperCode)) {
    const main = TELEGRAM_GROUPS_EN_PLAYBACK_ID || TELEGRAM_GROUPS_PLAYBACK_ID || FALLBACK_PLAYBACK_ID;
    if (!main || main.trim().length === 0) {
      console.error(`[telegramGroupsVideo] All playback IDs empty for ${upperCode} - critical config error`);
      return FALLBACK_PLAYBACK_ID;
    }
    return main;
  }
  
  // Default: English video (for unknown countries), fallback to hardcoded
  const main = TELEGRAM_GROUPS_EN_PLAYBACK_ID || TELEGRAM_GROUPS_PLAYBACK_ID || FALLBACK_PLAYBACK_ID;
  if (!main || main.trim().length === 0) {
    console.error(`[telegramGroupsVideo] All playback IDs empty for unknown country ${upperCode} - critical config error`);
    return FALLBACK_PLAYBACK_ID;
  }
  return main;
}

// =============================================================================
// MAIN CONFIGURATION FUNCTION
// =============================================================================

// Todos los países BioLinkTemplate + fallbacks
const TELEGRAM_PLAYBACK_COUNTRIES = [
  "US", "ES", "DE", "FR", "IT", "PT", "NL", "BE", "AT", "CH", "GB", "IE",
  "SE", "NO", "DK", "FI", "PL", "CZ", "GR", "RO", "HU", "SK", "BG", "HR",
  "SI", "LT", "LV", "EE", "LU", "MT", "CY", "IS", // Europe + Iceland
  "MX", "CO", "PE", "PY", "BO", "CL", "BR", "UY", "AR", "AE", "SA", "LATAM",
] as const;

/**
 * Get Telegram Groups video configuration for all countries
 * Garantiza que siempre haya un playbackId válido usando FALLBACK_PLAYBACK_ID si es necesario
 */
export function getTelegramGroupsVideoConfig() {
  const defaultId = TELEGRAM_GROUPS_PLAYBACK_ID || TELEGRAM_GROUPS_EN_PLAYBACK_ID || FALLBACK_PLAYBACK_ID;
  const playbackIds: Record<string, string> = {
    default: defaultId,
  };
  for (const cc of TELEGRAM_PLAYBACK_COUNTRIES) {
    const id = getTelegramPlaybackIdByCountry(cc) || defaultId;
    playbackIds[cc] = id;
  }

  return {
    playbackIds,
    ctaUrls: {
      // HIGH PRICE ($29.99) - US
      US: TELEGRAM_GROUPS_US_CTA_URL,

      // HIGH PRICE ($29.99) - Spain (Spanish-speaking Europe)
      ES: TELEGRAM_GROUPS_ES_CTA_URL,

      // HIGH PRICE ($29.99) - Europe (English video)
      DE: HIGH_PRICE_URL,
      FR: HIGH_PRICE_URL,
      IT: HIGH_PRICE_URL,
      PT: HIGH_PRICE_URL,
      NL: HIGH_PRICE_URL,
      BE: HIGH_PRICE_URL,
      AT: HIGH_PRICE_URL,
      CH: HIGH_PRICE_URL,
      GB: HIGH_PRICE_URL,
      IE: HIGH_PRICE_URL,
      SE: HIGH_PRICE_URL,
      NO: HIGH_PRICE_URL,
      DK: HIGH_PRICE_URL,
      FI: HIGH_PRICE_URL,
      PL: HIGH_PRICE_URL,
      CZ: HIGH_PRICE_URL,
      GR: HIGH_PRICE_URL,
      RO: HIGH_PRICE_URL,
      HU: HIGH_PRICE_URL,
      SK: HIGH_PRICE_URL,
      BG: HIGH_PRICE_URL,
      HR: HIGH_PRICE_URL,
      SI: HIGH_PRICE_URL,
      LT: HIGH_PRICE_URL,
      LV: HIGH_PRICE_URL,
      EE: HIGH_PRICE_URL,
      LU: HIGH_PRICE_URL,
      MT: HIGH_PRICE_URL,
      CY: HIGH_PRICE_URL,
      IS: HIGH_PRICE_URL, // Iceland - BioLinkTemplate

      // HIGH PRICE ($29.99) - LATAM
      MX: TELEGRAM_GROUPS_MX_CTA_URL,
      CO: HIGH_PRICE_URL,
      PE: HIGH_PRICE_URL,
      PY: HIGH_PRICE_URL,
      BO: HIGH_PRICE_URL,

      // HIGH PRICE ($29.99) - Middle East
      AE: TELEGRAM_GROUPS_AE_CTA_URL,
      SA: TELEGRAM_GROUPS_SA_CTA_URL,

      // LOW PRICE - Chile, Brasil, Uruguay
      CL: LOW_PRICE_URL,
      BR: LOW_PRICE_URL,
      UY: LOW_PRICE_URL,

      // ARGENTINA - env var or fallback
      AR: TELEGRAM_GROUPS_AR_CTA_URL || ARGENTINA_FALLBACK_URL,

      // LATAM fallback (Worker sin país específico) - Spanish video, high price
      LATAM: HIGH_PRICE_URL,

      // DEFAULT - High price for unlisted countries
      default: HIGH_PRICE_URL,
    },
  };
}

/**
 * Get CTA URL for a specific country
 * Automatically appends client_reference_id=BRAND_SOURCE_KEY for Stripe tracking
 */
export function getTelegramCTAUrlByCountry(countryCode: string): string {
  const config = getTelegramGroupsVideoConfig();
  const upperCode = countryCode.toUpperCase();

  let url: string;
  if (upperCode in config.ctaUrls) {
    url = config.ctaUrls[upperCode as keyof typeof config.ctaUrls];
  } else {
    url = config.ctaUrls.default;
  }

  // Append brand reference ID for tracking
  return appendBrandReferenceId(url);
}

/**
 * Get playback ID for a specific country.
 *
 * Priority:
 * 1. ES (Spain): use default Spanish playback.
 * 2. Country-specific env var (VITE_TELEGRAM_GROUPS_XX_PLAYBACK_ID) for overrides.
 * 3. Language-based fallback (Spanish-speaking → Spanish, US/EU/ME → English).
 *
 * La visibilidad/geo se maneja en Cloudflare; el worker elige template por país.
 * Este config solo selecciona playback por idioma/región.
 * 
 * GARANTIZA que siempre retorne un playbackId válido (nunca vacío).
 */
export function getTelegramPlaybackIdByCountry(countryCode: string): string {
  const upperCode = countryCode.toUpperCase();

  // Spain: use main Spanish video
  if (upperCode === "ES") {
    const playbackId = getDefaultPlaybackIdForCountry(upperCode);
    if (!playbackId || playbackId.trim().length === 0) {
      console.error(`[telegramGroupsVideo] Empty playbackId for ES - using FALLBACK`);
      return FALLBACK_PLAYBACK_ID;
    }
    return playbackId;
  }

  // Check for country-specific env var
  const countryEnvVar = getEnvVar(
    import.meta.env[`VITE_TELEGRAM_GROUPS_${upperCode}_PLAYBACK_ID`] as string
  );

  if (countryEnvVar) {
    if (import.meta.env.DEV) {
      console.log(`[telegramGroupsVideo] Using country-specific playback for ${upperCode}`);
    }
    return countryEnvVar;
  }

  // Fallback based on language/region
  const playbackId = getDefaultPlaybackIdForCountry(upperCode);
  
  // CRITICAL: Ensure we never return empty playbackId
  if (!playbackId || playbackId.trim().length === 0) {
    console.error(`[telegramGroupsVideo] Empty playbackId for ${upperCode} - using FALLBACK`);
    return FALLBACK_PLAYBACK_ID;
  }
  
  return playbackId;
}

/** Playback ID garantizado para /ar/invitacion (channel_invite). Nunca vacío. Prioridad: VITE_TELEGRAM_GROUPS_AR_PLAYBACK_ID > español/AR > fallback. */
export function getChannelInvitePlaybackIdForAR(): string {
  return AR_PLAYBACK_ID || getTelegramPlaybackIdByCountry("AR") || FALLBACK_PLAYBACK_ID;
}

/** Playback ID para la opción VIP en /ar/invitacion. Si no está definido VITE_AR_INVITACION_VIP_PLAYBACK_ID, usa el estándar AR. */
export function getChannelInviteVipPlaybackIdForAR(): string {
  return AR_VIP_PLAYBACK_ID || getChannelInvitePlaybackIdForAR();
}

/**
 * Get playback ID directly by language code.
 * @param language - Language code like "es", "en", "es-AR", "en-US"
 */
export function getTelegramPlaybackIdByLanguage(language: string): string {
  const baseLang = language.split("-")[0].toLowerCase();
  
  if (baseLang === "es") {
    return TELEGRAM_GROUPS_PLAYBACK_ID;
  }
  
  // English and all other languages
  return TELEGRAM_GROUPS_EN_PLAYBACK_ID;
}
